// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a frame held as a baseline, and every later frame read as a
 * difference from it (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, slice 2).
 *
 * "How different is this from last year?" is the question a statistic of
 * one frame cannot answer. The Analyze panel holds one frame's statistics
 * and latitude profile here; on any later frame of the same dataset, over
 * the same texels, each number is shown with its difference.
 *
 * Two rules keep a difference honest:
 *
 * - **Same texels and units, or nothing.** A baseline applies only to
 *   the texel window it was measured on (`regionKey`) and in the same
 *   units. "What I can see" moves with the camera, so the same scope name
 *   is not the same region; comparing over different texels would report
 *   the camera's movement as a change in the field. Another dataset on
 *   the same grid and units may be compared (SSP5-8.5 against SSP1-2.6 in
 *   the same year is the comparison a researcher wants); the panel names
 *   the baseline's dataset when it is not the one on the globe.
 * - **Within one step is not a change.** Values come from 8-bit codes, so
 *   a difference smaller than the quantisation step is shown as within
 *   resolution rather than as a number that looks like a measurement.
 *
 * Pure: no DOM.
 */

import type { RegionStats, ZonalSample } from '../datasetStats'
import type { ColorScale } from '../../types/color-scale'
import type { DatasetOverlayOptions } from '../../types'

/** The statistics a baseline compares, in the order the panel shows them. */
export const BASELINE_STATS = ['mean', 'median', 'min', 'max', 'p10', 'p90', 'stdDev', 'areaKm2'] as const
export type BaselineStat = (typeof BASELINE_STATS)[number]

export interface Baseline {
  readonly datasetId: string
  readonly datasetTitle: string | null
  /** The scope and its texel window (`regionKey`): differences need the same texels. */
  readonly regionKey: string
  /** The region as the panel names it, for "held for …" when it no longer applies. */
  readonly regionLabel: string
  /** The frame's time as the panel shows it; null for a dataset without dates. */
  readonly frameLabel: string | null
  readonly units?: string
  readonly stats: RegionStats
  readonly zonal: readonly ZonalSample[] | null
  /**
   * The whole frame's codes, kept for the change map on the globe
   * (`changeMap.ts`): a copy, since the panel's snapshot is reused.
   */
  readonly field?: {
    readonly data: Uint8Array
    readonly width: number
    readonly height: number
    readonly scale: ColorScale
    /** Where the frame sits on the Earth (`gridKey`): the same key, the same texels. */
    readonly gridKey: string
  }
}

/** Where a frame's texels sit on the Earth: two frames with the same key pair texel by texel. */
export function gridKey(width: number, height: number, options: DatasetOverlayOptions | undefined): string {
  return JSON.stringify([width, height, options?.boundingBox ?? null, options?.lonOrigin ?? 0, options?.isFlippedInY === true, options?.cropRect ?? null])
}

/** One key for a scope and the texels it covered; equal keys mean the same texels. */
export function regionKey(scopeValue: string, window: unknown): string {
  return `${scopeValue}|${window === undefined || window === null ? 'all' : JSON.stringify(window)}`
}

/** AYNI: `other-grid` / `other-calendar`: a period's climatology on another grid, or for a frame that cannot be placed in its calendar (it covers every region of its own). */
export type BaselineFit = 'applies' | 'other-region' | 'other-units' | 'other-grid' | 'other-calendar'

/** Whether a baseline can be compared with a frame over this region, in these units. */
export function baselineFit(base: Baseline, key: string, units: string | undefined): BaselineFit {
  if ((base.units ?? '') !== (units ?? '')) return 'other-units'
  return base.regionKey === key ? 'applies' : 'other-region'
}

/** Each statistic's difference, now minus baseline. */
export function statDeltas(now: RegionStats, base: RegionStats): Record<BaselineStat, number> {
  const out = {} as Record<BaselineStat, number>
  for (const k of BASELINE_STATS) out[k] = now[k] - base[k]
  return out
}

/**
 * Whether a difference is inside the data's resolution: smaller than one
 * quantisation step. Area is not quantised in value, so it never is.
 */
export function withinResolution(stat: BaselineStat, delta: number, step: number | null): boolean {
  if (stat === 'areaKm2' || step === null || !(step > 0)) return false
  return Math.abs(delta) < step
}

export interface ZonalDifference {
  readonly lat: number
  /** Now minus baseline; null where either frame has no data in that band. */
  readonly delta: number | null
}

/**
 * The latitude profile's difference, band by band. The same texel window
 * gives the same rows, so bands pair by index; a profile of another shape
 * (another region) gives none.
 */
export function zonalDifference(now: readonly ZonalSample[], base: readonly ZonalSample[]): ZonalDifference[] | null {
  if (now.length !== base.length || now.length === 0) return null
  const out: ZonalDifference[] = []
  for (let i = 0; i < now.length; i++) {
    if (Math.abs(now[i].lat - base[i].lat) > 1e-6) return null
    const a = now[i].mean
    const b = base[i].mean
    out.push({ lat: now[i].lat, delta: a === null || b === null ? null : a - b })
  }
  return out
}

/** The band whose mean changed most, either way; null when no band has both frames. */
export function largestZonalChange(diff: readonly ZonalDifference[]): ZonalDifference | null {
  let best: ZonalDifference | null = null
  for (const d of diff) {
    if (d.delta === null) continue
    if (!best || Math.abs(d.delta) > Math.abs(best.delta!)) best = d
  }
  return best
}
